import { capitalizeFirstLetter } from '@resistapp/common/friendly';
import { CtCsvRow, ctsHeaders } from '@resistapp/common/types';
import { Dictionary, get, sortBy } from 'lodash';
import { tsvFormatRows } from 'd3-dsv';

/**
 * Build the tab separated CTS text file content (including Windows line endings) for the given rows.
 *
 * Preserve the current Takara columns when present, and the stored row-ID order.
 * If `qaById` is provided a final
 * column "Qa" will be appended to the header and the corresponding values from the mapping
 * are appended to each row (empty string if a value is not found).
 */
export function buildCtsFileContent(
  rows: Array<CtCsvRow & { id?: number }>,
  options: { qaById?: Dictionary<string | number | null>; format?: 'auto' | 'legacy' | 'takara' } = {},
): string {
  const { qaById } = options;
  const includeQa = qaById !== undefined;

  const linebreak = '\r\n';
  const currentFormat =
    options.format === 'takara' ||
    (options.format !== 'legacy' && rows.some(row => row.idx != null || row.dye != null || row.curveQuality != null));
  const keys: Array<keyof CtCsvRow> = currentFormat
    ? ['idx', 'dye', 'row', 'column', 'assay', 'sample', 'conc', 'ct', 'tm', 'efficiency', 'curveQuality', 'flags']
    : ctsHeaders;
  const headers = currentFormat
    ? ['Idx', 'Dye', 'Row', 'Column', 'Assay', 'Sample', 'Conc', 'Cq', 'Tm', 'Efficiency', 'CurveQuality', 'Flags']
    : ctsHeaders.map(capitalizeFirstLetter);
  const headerRow = [...headers, ...(includeQa ? ['Qa'] : [])].join('\t') + linebreak;

  const dataRows = sortBy(rows, r => r.id ?? 0)
    .map(row => {
      // Gather base CTS values in the defined header order
      const baseValues = keys.map(key => (row[key] == null ? '' : String(row[key])));
      if (includeQa && row.id) {
        const qaValue = get(qaById, row.id, '');
        return tsvFormatRows([[...baseValues, qaValue == null ? '' : String(qaValue)]]);
      }
      return tsvFormatRows([baseValues]);
    })
    .join(linebreak);

  return headerRow + dataRows;
}
